import type { BinderVariant } from "@/types/binders";

/**
 * Returns a binder copy's identity-matched market value.
 *
 * Graded copies must never fall back to the raw variant price. A missing
 * grade/company price means the graded copy is currently unpriced.
 */
export function getBinderItemMarketValue(
  binderItem: BinderVariant,
): number | null {
  if (binderItem.pricing) return binderItem.pricing.value;
  if (binderItem.resolved_true_price !== undefined)
    return binderItem.resolved_true_price;
  // Missing canonical evidence is unavailable, even if a legacy guide exists.
  return null;
}

export function getBinderItemPriceLabel(binderItem: BinderVariant): string {
  if (binderItem.pricing) {
    const identity = binderItem.pricing.identity;
    return [
      identity.language,
      identity.gradingCompany,
      identity.grade,
      identity.condition,
      identity.printing,
      "evidence-backed market estimate",
    ]
      .filter(Boolean)
      .join(" · ");
  }
  const language =
    binderItem.language.charAt(0).toUpperCase() + binderItem.language.slice(1);

  if (binderItem.grading_company && binderItem.grade) {
    return `${language} · ${binderItem.grading_company.name} ${binderItem.grade.name} price guide`;
  }

  const condition = binderItem.variant.condition?.name;
  return condition
    ? `${language} · Raw ${condition} market reference`
    : `${language} · Raw market reference`;
}
